import { normalizeLiveTimeframe } from "../redux/live-suggestion/liveSuggestionSlice";

const STRENGTH_LABELS = {
  1: "Faint",
  2: "Feeble",
  3: "Fair",
  4: "Robust",
  5: "Powerful",
};

function sideOf(value) {
  const side = String(value || "").split("/")[0];
  return ["Buy", "Sell", "Hold"].includes(side) ? side : "";
}

function coinKey(value) {
  return String(value || "")
    .split("/")[0]
    .toUpperCase();
}

function pad(value, width) {
  const text = String(value ?? "");
  if (text.length >= width) return text;
  return text + " ".repeat(width - text.length);
}

function strengthWord(value) {
  if (value === 0 || value === "0") return "—";
  if (!value && value !== 0) return "—";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return STRENGTH_LABELS[Math.round(n)] || String(value);
  const text = String(value).trim();
  return text || "—";
}

function confidenceText(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const pct = n > 1 ? n : n * 100;
  return `${pct.toFixed(0)}%`;
}

function liveText(row) {
  return row.live_label || sideOf(row.live_side) || "Hold";
}

function pivotText(row) {
  return row.pivot === "on" || row.pivot_detected ? "on" : "off";
}

function liveCallText(pivot, live) {
  if (pivot !== "on") return "No pivot";
  const side = sideOf(live);
  return side && side !== "Hold" ? side : "Pivot";
}

function formatUtc(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  const h = String(date.getUTCHours()).padStart(2, "0");
  const min = String(date.getUTCMinutes()).padStart(2, "0");
  return `${y}-${m}-${d} ${h}:${min} UTC`;
}

function mixCounts(visual) {
  const actions = visual?.actions || {};
  return {
    buy: Number(actions.Buy) || 0,
    sell: Number(actions.Sell) || 0,
    hold: Number(actions.Hold) || 0,
  };
}

function biasLabel(buy, sell) {
  if (sell > buy * 1.5) return "Short-biased";
  if (buy > sell * 1.5) return "Long-biased";
  if (sell > buy) return "Mildly short-biased";
  if (buy > sell) return "Mildly long-biased";
  return "Neutral";
}

function isBoilerplate(text) {
  const value = String(text || "");
  return (
    value.includes("Board mix is") ||
    value.includes("Board Buy on") ||
    value.includes("Board Sell on") ||
    value.includes("15m live disagrees")
  );
}

function takeaway(sections, summaryText, pivots) {
  if (summaryText && !isBoilerplate(summaryText) && summaryText.length < 280) {
    return summaryText.split("\n")[0];
  }
  if (sections?.opportunities && !isBoilerplate(sections.opportunities)) {
    return String(sections.opportunities).trim();
  }
  if (pivots.length) {
    return `Pivot turns on ${[...new Set(pivots.map((item) => coinKey(item.symbol)))].join(", ")}.`;
  }
  return "No pivot on this board.";
}

function insightCoins(visual) {
  const byCoin = {};
  (visual?.rows || []).forEach((row) => {
    const coin = coinKey(row.coin);
    if (!coin) return;
    const current = byCoin[coin] || {
      coin,
      buy: false,
      sell: false,
      hold: false,
      gap: false,
      strength: "",
      live: "",
      pivot: "off",
      conflict: false,
    };
    const side = sideOf(row.suggestion) || "Hold";
    if (side === "Buy") current.buy = true;
    else if (side === "Sell") current.sell = true;
    else current.hold = true;
    if (row.data_gap) current.gap = true;
    if (!current.strength && row.strength) current.strength = row.strength;
    if (row.live_label || row.live_side) current.live = liveText(row);
    if (pivotText(row) === "on") current.pivot = "on";
    if (row.conflict) current.conflict = true;
    byCoin[coin] = current;
  });
  return Object.values(byCoin);
}

function assistantSymbols(visual) {
  return (visual?.symbols || []).map((row) => ({
    symbol: coinKey(row.symbol),
    board: sideOf(row.action) || "Hold",
    live: liveText(row),
    conf: confidenceText(row.live_confidence),
    strength: row.strength_label || strengthWord(row.strength),
    pivot: pivotText(row),
    conflict: Boolean(row.conflict),
  }));
}

function rule() {
  return "────────────────────────────────────────";
}

function buildInsightSnapshot({ summaryText, sections, visualSummary, freshness }) {
  const visual = visualSummary || {};
  const coins = insightCoins(visual);
  const mix = mixCounts(visual);
  const pivots = Array.isArray(visual.pivots) ? visual.pivots : [];
  const longs = coins.filter((item) => item.buy && !item.sell);
  const shorts = coins.filter((item) => item.sell && !item.buy);
  const both = coins.filter((item) => item.buy && item.sell);
  const gaps = coins.filter((item) => item.gap).map((item) => item.coin);
  const conflicts = coins.filter((item) => {
    const live = sideOf(item.live);
    const board = item.buy && !item.sell ? "Buy" : item.sell && !item.buy ? "Sell" : "Hold";
    return item.conflict || (live && board && live !== board);
  });
  const lines = [
    "### Board Snapshot",
    `Time: ${formatUtc(freshness?.generated_at || freshness?.data_as_of) || "—"}`,
    `Universe: ${coins.length || mix.buy + mix.sell + mix.hold} symbols`,
    "",
    rule(),
    "OVERALL STANCE",
    `Bias: ${biasLabel(mix.buy, mix.sell)}`,
    `Mix: ${mix.buy} Buy · ${mix.sell} Sell · ${mix.hold} Hold`,
    "",
    "Key takeaway:",
    takeaway(sections, summaryText, pivots),
    "",
    rule(),
    "ACTIVE SIGNALS",
    "",
    "Longs",
  ];
  if (longs.length) {
    longs.forEach((item) => {
      lines.push(
        `• ${pad(item.coin, 8)}${pad("Buy", 7)}Strength: ${pad(item.strength || "—", 7)} Pivot: ${pad(liveCallText(item.pivot, item.live), 14)}`,
      );
    });
  } else {
    lines.push("• None");
  }
  lines.push("", "Shorts");
  if (shorts.length || both.length) {
    [...shorts, ...both].forEach((item) => {
      lines.push(
        `• ${pad(item.coin, 8)}${pad("Sell", 7)}Strength: ${pad(item.strength || "—", 7)} Pivot: ${pad(liveCallText(item.pivot, item.live), 14)}`,
      );
    });
  } else {
    lines.push("• None");
  }
  lines.push("", rule(), "LIVE AI");
  if (pivots.length) {
    const names = [...new Set(pivots.map((item) => coinKey(item.symbol)))];
    lines.push(`Live calls: ${names.join(" · ")}`);
  } else {
    lines.push("No other live calls");
  }
  lines.push("", rule(), "CONFLICTS");
  if (conflicts.length) {
    conflicts.forEach((item) => {
      const board = item.buy && item.sell ? "Buy/Sell" : item.buy ? "Buy" : item.sell ? "Sell" : "Hold";
      lines.push(`• ${pad(item.coin, 8)}Board ${board}  vs  Live ${item.live || "Hold"}`);
    });
  } else {
    lines.push("No board vs 15m disagreements.");
  }
  lines.push("", rule(), "DATA GAPS");
  lines.push(gaps.length ? gaps.join(", ") : "None");
  lines.push("", rule(), "NOTES");
  if (both.length) {
    lines.push(
      `- ${both.map((item) => item.coin).join(", ")} appear as both Buy and Sell in source → treat with caution`,
    );
  }
  lines.push("- Holds and pure gap symbols omitted from Active Signals for clarity");
  return lines.join("\n").trim();
}

function buildAssistantSnapshot({ summaryText, sections, visualSummary, freshness }) {
  const visual = visualSummary || {};
  const symbols = assistantSymbols(visual);
  const mix = mixCounts(visual);
  const pivots = Array.isArray(visual.pivots) ? visual.pivots : [];
  const longs = symbols.filter((item) => item.board === "Buy");
  const shorts = symbols.filter((item) => item.board === "Sell");
  const active = longs.length + shorts.length;
  const conflicts = symbols.filter((item) => {
    const live = sideOf(item.live);
    return item.conflict || (live && item.board && live !== item.board);
  });
  const lines = [
    "### Board Snapshot",
    `Time: ${formatUtc(freshness?.generated_at || freshness?.data_as_of) || "—"}`,
    `Next refresh: ${freshness?.next_refresh_at || "—"}`,
    `Universe: ${symbols.length} symbols (${active} with active signals)`,
    "",
    rule(),
    "OVERALL STANCE",
    `Bias: ${biasLabel(mix.buy, mix.sell)}`,
    `Mix: ${mix.buy} Buy · ${mix.sell} Sell · ${mix.hold} Hold`,
    "",
    "Key takeaway:",
    takeaway(sections, summaryText, pivots),
    "",
    rule(),
    "ACTIVE SIGNALS",
    "",
    "Longs",
  ];
  if (longs.length) {
    longs.forEach((item) => {
      lines.push(
        `• ${pad(item.symbol, 8)}${pad("Buy", 7)}Strength: ${pad(item.strength, 8)} Pivot: ${pad(liveCallText(item.pivot, item.live), 14)} Conf: ${pad(item.conf, 5)}`,
      );
    });
  } else {
    lines.push("• None");
  }
  lines.push("", "Shorts");
  if (shorts.length) {
    shorts.forEach((item) => {
      lines.push(
        `• ${pad(item.symbol, 8)}${pad("Sell", 7)}Strength: ${pad(item.strength, 8)} Pivot: ${pad(liveCallText(item.pivot, item.live), 14)} Conf: ${pad(item.conf, 5)}`,
      );
    });
  } else {
    lines.push("• None");
  }
  lines.push("", rule(), "LIVE AI");
  if (pivots.length) {
    lines.push("Live calls:");
    pivots.forEach((item) => {
      lines.push(
        `• ${pad(coinKey(item.symbol), 8)}${item.live_side || liveText(item)}   (live call)`,
      );
    });
  } else {
    lines.push("No other live calls");
  }
  lines.push("", rule(), "CONFLICTS");
  if (conflicts.length) {
    conflicts.forEach((item) => {
      lines.push(`• ${pad(item.symbol, 8)}Board ${item.board}  vs  Live ${item.live}`);
    });
  } else {
    lines.push("No board vs 15m disagreements.");
  }
  lines.push("", rule(), "DETAIL TABLE", "");
  lines.push("Symbol  | Board | Pivot         | Conf | Strength");
  lines.push("--------|-------|---------------|------|----------");
  symbols.forEach((item) => {
    lines.push(
      `${pad(item.symbol, 8)}| ${pad(item.board, 6)}| ${pad(liveCallText(item.pivot, item.live), 14)}| ${pad(item.conf, 5)}| ${item.strength}`,
    );
  });
  lines.push("", rule(), "NOTES");
  const strengths = [...new Set(symbols.map((item) => item.strength).filter((item) => item && item !== "—"))];
  if (strengths.length === 1) lines.push(`- All strengths currently ${strengths[0]}`);
  const topConf = [...symbols].sort((a, b) => parseFloat(b.conf) - parseFloat(a.conf))[0];
  if (topConf && topConf.conf !== "—") {
    lines.push(`- Highest confidence: ${topConf.symbol} (${topConf.conf})`);
  }
  return lines.join("\n").trim();
}

export function boardClipboardText({
  variant = "assistant",
  summaryText,
  sections,
  visualSummary,
  freshness,
}) {
  if (variant === "insight") {
    return buildInsightSnapshot({ summaryText, sections, visualSummary, freshness });
  }
  return buildAssistantSnapshot({ summaryText, sections, visualSummary, freshness });
}

export function boardDisplay(text) {
  const lines = String(text || "").split("\n");
  const bias = (lines.find((line) => line.startsWith("Bias:")) || "").replace(/^Bias:\s*/, "");
  const mix = (lines.find((line) => line.startsWith("Mix:")) || "").replace(/^Mix:\s*/, "");
  const takeIdx = lines.findIndex((line) => line === "Key takeaway:");
  const takeawayLine = takeIdx >= 0 ? lines[takeIdx + 1] || "" : "";
  const restStart = lines.findIndex((line, index) => index > takeIdx && line.startsWith("──"));
  const blocks = [];
  let current = null;
  (restStart >= 0 ? lines.slice(restStart) : []).forEach((line) => {
    if (line.startsWith("──")) {
      if (current) blocks.push(current);
      current = null;
      return;
    }
    if (!current) {
      if (!line.trim()) return;
      current = { title: line.trim(), lines: [] };
      return;
    }
    if (!line.trim()) return;
    current.lines.push(line.replace(/[ \t]+/g, " ").trim());
  });
  if (current) blocks.push(current);
  return {
    bias,
    mix,
    takeaway: takeawayLine,
    blocks: blocks.filter((block) => block.title && block.lines.length),
  };
}

export function snapshotCsv(variant, visualSummary) {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  if (variant === "insight") {
    const liveName = normalizeLiveTimeframe(visualSummary?.live_timeframe);
    const rows = [["Coin", "Board", `Pivot ${liveName}`, "Strength"]];
    insightCoins(visualSummary).forEach((item) => {
      const board = item.buy && item.sell ? "Buy/Sell" : item.buy ? "Buy" : item.sell ? "Sell" : "Hold";
      rows.push([item.coin, board, liveCallText(item.pivot, item.live), item.strength || "—"]);
    });
    return rows.map((row) => row.map(escape).join(",")).join("\n");
  }
  const liveName = normalizeLiveTimeframe(visualSummary?.live_timeframe);
  const rows = [["Symbol", "Board", `Pivot ${liveName}`, "Confidence", "Strength"]];
  assistantSymbols(visualSummary).forEach((item) => {
    rows.push([item.symbol, item.board, liveCallText(item.pivot, item.live), item.conf, item.strength]);
  });
  return rows.map((row) => row.map(escape).join(",")).join("\n");
}

function htmlEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function snapshotHtml(title, text, variant, visualSummary) {
  const rows =
    variant === "insight"
      ? [
          ["Coin", "Board", "Pivot", "Strength"],
          ...insightCoins(visualSummary).map((item) => [
            item.coin,
            item.buy && item.sell ? "Buy/Sell" : item.buy ? "Buy" : item.sell ? "Sell" : "Hold",
            liveCallText(item.pivot, item.live),
            item.strength || "—",
          ]),
        ]
      : [
          ["Symbol", "Board", "Pivot", "Confidence", "Strength"],
          ...assistantSymbols(visualSummary).map((item) => [
            item.symbol,
            item.board,
            liveCallText(item.pivot, item.live),
            item.conf,
            item.strength,
          ]),
        ];
  const tableRows = rows
    .map((cells, index) => {
      const tag = index === 0 ? "th" : "td";
      return `<tr>${cells.map((cell) => `<${tag}>${htmlEscape(cell)}</${tag}>`).join("")}</tr>`;
    })
    .join("");
  return (
    `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${htmlEscape(title)}</title>` +
    `<style>body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111}` +
    `pre{background:#f7f9fc;padding:16px;border-radius:8px;white-space:pre-wrap;font-size:13px;line-height:1.45}` +
    `table{border-collapse:collapse;width:100%;margin-top:20px;font-size:13px}` +
    `th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}th{background:#eee}</style></head>` +
    `<body><pre>${htmlEscape(text)}</pre><table>${tableRows}</table></body></html>`
  );
}

export function snapshotJson({ summary, visualSummary, freshness, text }) {
  return JSON.stringify(
    {
      Summary: summary || {},
      visual_summary: visualSummary || {},
      freshness: freshness || {},
      snapshot: text,
    },
    null,
    2,
  );
}

export function downloadTextFile(filename, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
